import { createPaintLayer, duplicateLayer, getLayer, updateLayer } from '../core/model/document';
import {
  layerStackAfterFlatten,
  type MergeDownRejection,
  planFlatten,
  planMergeDown,
  rasterSourceIds,
} from '../core/model/merge';
import {
  countLeaves,
  findLayer,
  insertNextTo,
  isGroup,
  type LayerLocation,
  leafContexts,
  locateLayer,
  moveSiblingRuns,
  parentGroup,
  pruneSubtrees,
  replaceSiblings,
  rowOrder,
} from '../core/model/tree';
import type { ImageLayer, Layer } from '../core/model/types';
import { isMovable, nudge } from '../core/move';
import { blankLayerDataUrl, rasterizeRegion } from '../io/raster';
import type { AppContext } from './context';
import { refuseMultiSelection } from './context';
import { beginDocumentEdit, STALE_EDIT_MESSAGE } from './staleGuard';

/**
 * Layer operations, each committed as one history entry.
 *
 * Merging is the interesting one: `core/model/merge` decides the region and the
 * sources, `io/raster` paints them, and this file reassembles the document.
 * The merged layer carries opacity 1 and normal blending because both layers'
 * alpha and blend mode are already baked into its pixels.
 *
 * The stack is a **tree** (see `core/model/tree.ts`), so every operation here
 * works on the list the active layer actually lives in: a layer inside a group
 * is raised, merged and duplicated inside that group, and a new layer lands
 * next to the one you are working on rather than at the top of the document.
 */

const REJECTION_MESSAGES: Record<MergeDownRejection, string> = {
  'not-found': 'That layer no longer exists.',
  'bottom-layer': 'The bottom layer has nothing to merge into.',
  locked: 'The layer below is locked.',
  unsupported: 'These layer types cannot be merged yet.',
  composite: 'Ungroup, reset the group\u2019s opacity, or unclip the layers first.',
};

function activeLayer(ctx: AppContext) {
  const { doc, activeLayerId } = ctx.store.getState();
  return activeLayerId ? (getLayer(doc, activeLayerId) ?? null) : null;
}

/**
 * The selection, in the order the panel draws it — topmost first.
 *
 * Every set-aware command folds over this order, so "duplicate three layers"
 * and "delete four layers" do their work top-down and the history entry reads
 * the same regardless of the order the user happened to click the rows in.
 * Groups are members too: a selected group is selected with its contents.
 */
export function orderedSelection(ctx: AppContext): Layer[] {
  const { doc, selectedIds } = ctx.store.getState();
  // The store's invariant keeps the primary inside the set, so an empty set
  // really is "nothing is selected" — not a set that forgot its primary.
  if (selectedIds.size === 0) return [];
  const picked: Layer[] = [];
  for (const id of rowOrder(doc.layers)) {
    if (!selectedIds.has(id)) continue;
    const layer = findLayer(doc.layers, id);
    if (layer) picked.push(layer);
  }
  return picked;
}

/**
 * The selected layers the move verbs actually move: movable and unlocked.
 *
 * A selected group is its contents (Iteration 15): the group expands into its
 * leaf descendants — visible, unlocked, movable — so the keyboard nudges reach
 * into a group exactly as a canvas drag does. A locked group expands to
 * nothing, and a locked child is left behind while its unlocked siblings move;
 * a set naming both a group and one of its children moves each leaf once.
 */
function movableSelection(ctx: AppContext): Layer[] {
  const out: Layer[] = [];
  const seen = new Set<string>();
  for (const layer of orderedSelection(ctx)) {
    if (isGroup(layer)) {
      if (layer.locked) continue;
      // leafContexts walks the subtree with the ancestors' locks applied, so
      // a locked subgroup inside an unlocked group stops its own leaves.
      for (const context of leafContexts(layer.children)) {
        const leaf = context.layer;
        if (!context.unlocked || !isMovable(leaf) || seen.has(leaf.id)) continue;
        seen.add(leaf.id);
        out.push(leaf);
      }
      continue;
    }
    if (layer.locked || !isMovable(layer) || seen.has(layer.id)) continue;
    seen.add(layer.id);
    out.push(layer);
  }
  return out;
}

/**
 * Whether the move verbs have anything to act on: the selection names at
 * least one movable leaf or a group. **Locked counts as actable** — the nudge
 * runs so it can refuse out loud ("is locked"), the same honesty a canvas
 * drag offers; it is the pixel-less types (fill, adjustment) that have
 * nothing to move and disable the arrows honestly.
 */
export function hasMovableSelection(ctx: AppContext): boolean {
  return orderedSelection(ctx).some((layer) => isGroup(layer) || isMovable(layer));
}

/** The active layer and the list it lives in. */
function activeLocation(ctx: AppContext): { layer: Layer; location: LayerLocation } | null {
  const { doc, activeLayerId } = ctx.store.getState();
  if (!activeLayerId) return null;
  const location = locateLayer(doc.layers, activeLayerId);
  if (!location) return null;
  const layer = location.siblings[location.index];
  if (!layer) return null;
  return { layer, location };
}

/** A transparent layer the size of the canvas, inserted above the active one. */
export function addNewLayer(ctx: AppContext): void {
  const { doc, activeLayerId, commit, setActiveLayer, notify } = ctx.store.getState();
  const layer = createPaintLayer({
    name: `Layer ${doc.layers.length + 1}`,
    pixels: blankLayerDataUrl(doc.width, doc.height),
    naturalWidth: doc.width,
    naturalHeight: doc.height,
    x: 0,
    y: 0,
  });
  const next = {
    ...doc,
    layers: insertNextTo(doc.layers, layer, activeLayerId, 1),
  };
  commit('New Layer', next);
  setActiveLayer(layer.id);
  notify('New layer added');
}

export function duplicateActiveLayer(ctx: AppContext): void {
  const { doc, commit, assignSelection, notify } = ctx.store.getState();
  const picked = orderedSelection(ctx);
  if (picked.length === 0) return;
  // Fold in panel order (topmost first): each copy lands directly above its
  // source, so copies and sources interleave exactly as the stack did.
  let next = doc;
  const copyOf = new Map<string, string>();
  for (const layer of picked) {
    next = duplicateLayer(next, layer.id);
    const location = locateLayer(next.layers, layer.id);
    const copyId = location?.siblings[location.index + 1]?.id;
    if (copyId) copyOf.set(layer.id, copyId);
  }
  if (next === doc) return;
  const { activeLayerId } = ctx.store.getState();
  commit(picked.length > 1 ? `Duplicate ${picked.length} Layers` : 'Duplicate Layer', next);
  // The copies become the selection; the old primary's copy stays primary so
  // the handles keep pointing where the user was working.
  const primaryCopy = activeLayerId ? copyOf.get(activeLayerId) : undefined;
  assignSelection(copyOf.values(), primaryCopy);
  notify(
    picked.length > 1
      ? `Duplicated ${picked.length} layers`
      : `Duplicated “${picked[0]?.name ?? 'layer'}”`,
  );
}

export function deleteActiveLayer(ctx: AppContext): void {
  const { doc, commit, notify } = ctx.store.getState();
  const picked = orderedSelection(ctx);
  if (picked.length === 0) return;
  // A selected group is selected with its contents, so the removal prunes
  // whole subtrees — and a group the deletion emptied goes with them.
  const layers = pruneSubtrees(doc.layers, new Set(picked.map((layer) => layer.id)));
  if (layers === doc.layers) return;
  if (countLeaves(layers) === 0) {
    notify('A document needs at least one layer', 'warn');
    return;
  }
  commit(picked.length > 1 ? `Delete ${picked.length} Layers` : 'Delete Layer', {
    ...doc,
    layers,
  });
  notify(
    picked.length > 1
      ? `Deleted ${picked.length} layers`
      : `Deleted “${picked[0]?.name ?? 'layer'}”`,
  );
}

/**
 * Raise or lower the selection, as blocks.
 *
 * Each maximal run of consecutive selected siblings shifts one slot — moving
 * per layer would interleave the set with what the user deliberately left
 * alone. A selection spanning two lists moves each list's runs independently.
 * Nothing moves at a list's edge; a raise that moved nothing is no entry.
 */
export function moveActiveLayer(ctx: AppContext, direction: 1 | -1): void {
  const { doc, commit, selectedIds } = ctx.store.getState();
  if (selectedIds.size === 0) return;

  // One run-shift per sibling list the selection touches.
  const lists = new Map<string, { location: LayerLocation; ids: Set<string> }>();
  for (const id of selectedIds) {
    const location = locateLayer(doc.layers, id);
    if (!location) continue;
    const key = location.parent?.id ?? '';
    const entry = lists.get(key) ?? { location, ids: new Set<string>() };
    entry.ids.add(id);
    lists.set(key, entry);
  }

  let layers = doc.layers;
  let moved = false;
  for (const { location, ids } of lists.values()) {
    const shifted = moveSiblingRuns(location.siblings, ids, direction);
    if (shifted !== location.siblings) moved = true;
    layers = replaceSiblings(layers, location, shifted);
  }
  if (!moved) return;
  const count = selectedIds.size;
  const verb = direction === 1 ? 'Raise' : 'Lower';
  commit(count > 1 ? `${verb} ${count} Layers` : `${verb} Layer`, { ...doc, layers });
}

/**
 * Nudge the selection by whole document pixels — the arrow-key nudges.
 *
 * One press, one history entry: the same contract every raster editor uses,
 * so "undo the last nudge" means exactly what it says. Over a set, every
 * movable, unlocked member moves by the same delta and locked members are
 * left behind — the same rule a drag follows. A selected group is its
 * contents (Iteration 15), so the arrows reach into it.
 */
export function nudgeActiveLayer(ctx: AppContext, dx: number, dy: number): void {
  const { doc, commit, notify } = ctx.store.getState();
  const targets = movableSelection(ctx);
  if (targets.length === 0) {
    // The honest refusal: a locked primary is why nothing moved.
    const layer = activeLayer(ctx);
    if (layer?.locked) notify(`“${layer.name}” is locked`, 'warn');
    return;
  }
  let next = doc;
  for (const layer of targets) {
    next = updateLayer(next, layer.id, nudge(layer, dx, dy));
  }
  if (next === doc) return;
  commit(targets.length > 1 ? `Move ${targets.length} Layers` : 'Move Layer', next);
}

export async function mergeDownActive(ctx: AppContext): Promise<void> {
  // A merge is pairwise; over a set it refuses rather than secretly acting on
  // the primary (design doc §4). Merge Visible is the set-wide verb.
  if (refuseMultiSelection(ctx, 'Merge Down works on one layer — select just it')) return;
  const { doc, commit, setActiveLayer, notify } = ctx.store.getState();
  const active = activeLocation(ctx);
  if (!active) return;

  const { plan, reason } = planMergeDown(
    doc,
    active.location.siblings,
    active.location.index,
    parentGroup(doc.layers, active.location.siblings[active.location.index]?.id ?? ''),
  );
  if (!plan) {
    notify(REJECTION_MESSAGES[reason ?? 'not-found'], 'warn');
    return;
  }

  // The merge reads a captured `doc` and rebuilds it below; a commit that
  // landed inside the rasterise would be silently reverted by that rebuild.
  const documentStale = beginDocumentEdit(ctx);
  const src = await rasterizeRegion(plan.sources, plan.region);
  if (documentStale()) {
    ctx.notify(STALE_EDIT_MESSAGE, 'warn');
    return;
  }
  const target = active.location.siblings.find((candidate) => candidate.id === plan.targetId);
  const merged: ImageLayer = {
    ...(target ?? {}),
    id: plan.targetId,
    type: 'image',
    name: plan.name,
    visible: target?.visible ?? true,
    locked: target?.locked ?? false,
    opacity: 1,
    blendMode: 'normal',
    x: Math.round(plan.region.x),
    y: Math.round(plan.region.y),
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    clipping: false,
    // rasterizeRegion already baked the mask into the merged pixels;
    // keeping it here would make the engine (and every export) apply
    // the same coverage a second time.
    mask: null,
    src,
    naturalWidth: Math.max(1, Math.round(plan.region.width)),
    naturalHeight: Math.max(1, Math.round(plan.region.height)),
  };
  const next = active.location.siblings
    .map((candidate) => (candidate.id === plan.targetId ? merged : candidate))
    .filter((candidate) => candidate.id !== plan.removedId);

  commit('Merge Down', {
    ...doc,
    layers: replaceSiblings(doc.layers, active.location, next),
  });
  setActiveLayer(plan.targetId);
  notify('Layers merged');
}

/** Rasterise every visible layer into a single background layer. */
export async function flattenVisible(ctx: AppContext): Promise<void> {
  const { doc, commit, setActiveLayer, notify } = ctx.store.getState();
  const plan = planFlatten(doc);
  if (!plan) {
    notify('There is nothing visible to merge', 'warn');
    return;
  }

  // Same window as Merge Down: the flatten rebuilds a captured document.
  const documentStale = beginDocumentEdit(ctx);
  const src = await rasterizeRegion(plan.sources, plan.region, { background: plan.background });
  if (documentStale()) {
    ctx.notify(STALE_EDIT_MESSAGE, 'warn');
    return;
  }
  const merged: ImageLayer = {
    id: `${plan.name.toLowerCase()}-${Date.now().toString(36)}`,
    type: 'image',
    name: plan.name,
    visible: true,
    locked: false,
    opacity: 1,
    blendMode: 'normal',
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    clipping: false,
    mask: null,
    src,
    naturalWidth: plan.region.width,
    naturalHeight: plan.region.height,
  };

  // The merge takes the visible layers; hidden ones keep their slots instead
  // of being destroyed under a success toast.
  const mergedIds = rasterSourceIds(plan.sources);
  commit('Merge Visible', {
    ...doc,
    layers: layerStackAfterFlatten(doc, merged, mergedIds),
  });
  setActiveLayer(merged.id);
  notify('Visible layers merged');
}
